import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { API_BASE_URL, appActivityService } from '../services/api';

const AppActivity = () => {
  const [loading, setLoading] = useState(false);
  const [minutes, setMinutes] = useState(5);
  const [items, setItems] = useState([]);
  const [windowLabel, setWindowLabel] = useState(5);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const response = await appActivityService.getActive({ minutes, limit: 300 });
      const data = response.data || {};
      setItems(data.items || []);
      setWindowLabel(Number(data.activeWindowMinutes || minutes));
    } catch (error) {
      console.error('Erreur chargement activité app:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [minutes]);

  useEffect(() => {
    const interval = setInterval(fetchActivity, 15000);
    return () => clearInterval(interval);
  }, [minutes]);

  const registeredCount = useMemo(
    () => items.filter((it) => Boolean(it.isRegistered)).length,
    [items]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Activité application
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Utilisateurs actifs sur les {windowLabel} dernière(s) minute(s), page actuelle et derniers produits visités.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="activity-window-label">Fenêtre active</InputLabel>
            <Select
              labelId="activity-window-label"
              value={minutes}
              label="Fenêtre active"
              onChange={(e) => setMinutes(Number(e.target.value))}
            >
              <MenuItem value={1}>1 minute</MenuItem>
              <MenuItem value={5}>5 minutes</MenuItem>
              <MenuItem value={10}>10 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
            </Select>
          </FormControl>
          <Chip color="info" label={`Actifs: ${items.length}`} />
          <Chip color="success" label={`Inscrits: ${registeredCount}`} />
          <Chip variant="outlined" label={`Anonymes: ${Math.max(items.length - registeredCount, 0)}`} />
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Dernière activité</TableCell>
                  <TableCell>Inscrit</TableCell>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Page actuelle</TableCell>
                  <TableCell>Produits visités</TableCell>
                  <TableCell>Plateforme</TableCell>
                  <TableCell>Session</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 2 }}>
                        <CircularProgress size={22} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Aucun utilisateur actif
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((it) => (
                    <React.Fragment key={it.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            disabled={!Array.isArray(it.visitedProducts) || it.visitedProducts.length === 0}
                            onClick={() =>
                              setExpandedRows((prev) => ({ ...prev, [it.id]: !prev[it.id] }))
                            }
                          >
                            {expandedRows[it.id] ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {it.lastSeenAt ? new Date(it.lastSeenAt).toLocaleString('fr-FR') : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={it.isRegistered ? 'success' : 'default'}
                            label={it.isRegistered ? 'Oui' : 'Non'}
                          />
                        </TableCell>
                        <TableCell>
                          {it.user ? (
                            <>
                              {it.user.first_name} {it.user.last_name}
                              <br />
                              <Typography variant="caption" color="text.secondary">
                                {it.user.email}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Utilisateur anonyme
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{it.currentPage || '—'}</TableCell>
                        <TableCell>
                          {Array.isArray(it.visitedProducts) && it.visitedProducts.length > 0
                            ? `${it.visitedProducts.length} produit(s)`
                            : '—'}
                        </TableCell>
                        <TableCell>{it.platform || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>{it.sessionId}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={!!expandedRows[it.id]} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Produits visités récemment
                              </Typography>
                              {Array.isArray(it.visitedProducts) && it.visitedProducts.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                  {it.visitedProducts.map((p) => (
                                    <Box
                                      key={p.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.8,
                                        minWidth: 220,
                                      }}
                                    >
                                      <Avatar
                                        variant="rounded"
                                        src={p.image ? `${API_BASE_URL}${p.image}` : undefined}
                                        alt={p.name}
                                        sx={{ width: 42, height: 42 }}
                                      />
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {p.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ID: {p.id}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Aucun produit enregistré.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppActivity;

